import React from 'react'
import { Helmet } from 'react-helmet'
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'Front-End & UI',
    items: [
      { name: 'Front-End Developer (React/Vue/Angular)', icon: '🎨' },
      { name: 'TypeScript Engineer', icon: '📘' },
      { name: 'UI Engineer (HTML/CSS/Tailwind)', icon: '🖌️' },
      { name: 'Web Accessibility Specialist', icon: '♿' },
    ]
  },
  {
    title: 'Back-End & Full-Stack',
    items: [
      { name: 'Back-End Developer (Node/Python/.NET)', icon: '⚙️' },
      { name: 'Full-Stack Developer', icon: '🔄' },
      { name: 'Next.js / Nuxt.js Developer', icon: '🚀' },
      { name: 'REST & GraphQL API Developer', icon: '🔗' },
    ]
  },
  {
    title: 'Platform & Performance',
    items: [
      { name: 'DevOps / CI-CD Engineer', icon: '🛠️' },
      { name: 'Web Performance Engineer', icon: '⚡' },
      { name: 'WordPress Developer', icon: '📝' },
      { name: 'Shopify / E-Commerce Developer', icon: '🛒' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Stack-Specific Technical Expertise',
    text: 'Our recruiters are trained in modern web tech — React, Vue, Angular, Next.js, Node.js, Django, Laravel, .NET, and more. We assess candidates on real stack proficiency and project depth, not just resume keywords.',
  },
  {
    icon: '✔',
    title: 'Rigorous Multi-Layer Technical Vetting',
    text: 'All web developers are assessed for: hands-on proficiency in your specific front-end and back-end tech, code quality and architectural thinking, experience delivering production-grade web applications, API design, database skills, and version control discipline (Git workflows, CI/CD).',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Contract, contract-to-hire, or full-time. From a single developer for a feature sprint to a complete full-stack team for a product build — we structure engagements around your project requirements, budget, and timeline.',
  },
  {
    icon: '🛡',
    title: 'Performance & Security Standards',
    text: 'We screen for developers with proven knowledge of web performance optimization (Core Web Vitals, lazy loading, caching), responsive design, cross-browser compatibility, and secure coding practices — critical for production web applications.',
  },
];

const problemItems = [
  'Delayed product launches from developers unfamiliar with your stack, frameworks, or coding standards.',
  'Technical debt accumulation due to poor architecture decisions and unreviewed code that requires expensive rework.',
  'Poor UI/UX implementation from front-end developers who can\'t translate design specifications into responsive, performant interfaces.',
  'Scalability failures from back-end engineers lacking experience with high-traffic, production-grade web systems.',
  'Security vulnerabilities introduced by developers without secure coding practices or OWASP awareness.',
  'Budget overruns from extended timelines, failed sprints, and rework cycles driven by skill mismatches.',
];

const solutionPoints = [
  'Recruiters who understand the real distinctions between front-end, back-end, and full-stack roles',
  'Candidates assessed on actual coding ability, framework depth, and real project delivery experience',
  'Talent matched to your exact technology stack, not just a job title',
];

const outcomes = [
  {
    title: 'Faster product delivery',
    text: 'Skilled web developers who integrate into your agile team immediately and ship features on schedule.',
  },
  {
    title: 'Cleaner, scalable codebase',
    text: 'Engineers with strong architectural instincts who build for maintainability and growth from the start.',
  },
  {
    title: 'Better user experiences',
    text: 'Front-end specialists who build responsive, performant, and accessible interfaces that users love.',
  },
  {
    title: 'Reduced technical debt',
    text: 'Vetted developers who follow best practices, write testable code, and participate meaningfully in code reviews.',
  },
  {
    title: 'Accelerated digital roadmap',
    text: 'Eliminate talent bottlenecks and keep your web product development moving at the pace your business demands.',
  },
  {
    title: 'Scalable developer pipeline',
    text: 'Access a ready bench of pre-vetted web developers aligned to your technology stack and team growth plan.',
  },
];

const WebStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>Web & App Development India | Full-Stack Developers | Kapil Technologies</title>
        <meta name="description" content="Kapil Technologies provides expert web and mobile app development staffing in India. Hire React, Node.js, Python, Java, and mobile developers for your projects." />
        <meta name="keywords" content="web development India, app development India, full-stack developers India, React developers India, Node.js developers India, Python developers India, Java developers India, mobile app developers, iOS developers India, Android developers India, web app staffing, front-end developers India, back-end developers India, software development India, Kapil Technologies web" />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Web & App Development India | Full-Stack Developers | Kapil Technologies" />
        <meta property="og:description" content="Kapil Technologies provides expert web and mobile app development staffing in India. Hire React, Node.js, Python, and mobile developers." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Web & App Development India | Full-Stack Developers" />
        <meta name="twitter:description" content="Expert web and mobile app development staffing in India. Hire React, Node.js, Python, and mobile developers." />
        <link rel="canonical" href="https://kapiltechnologies.com/staff-augmentation/web-app" />
      </Helmet>

      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602851/Kapil_Tech_WebDeveloper_Staffing_k047ae.mp4"
        titleWhite="Full-Stack, Front-End & Back-End Web Talent."
        titleGradient="Vetted. Scalable. Delivered Fast."
      />

      <TechWhyChoose 
        title="Can't Find the Right Web Developers for Your Digital Products?"
        description="Kapil Tech connects businesses with pre-vetted web development professionals — from React and Angular front-end engineers to Node.js, Python, and .NET back-end developers, full-stack architects, and e-commerce specialists. Stop wasting time on unqualified candidates. Start building exceptional digital experiences."
        stats={[
          { value: "450+", label: "Web Developers Placed" },
          { value: "97%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_WebDeveloper_Staffing.jpg?updatedAt=1781603014168"
        imageAlt="Web Developer Staffing by Kapil Tech"
      />

      <TechRoles 
        title="Web Development Roles & Specializations We Staff"
        description="Our web development talent network spans every layer of the modern web stack."
        roleGroups={roleGroups}
      />

      <TechStaffingServices 
        title="Why Growing Companies Choose Kapil Tech for Web Developer Staffing"
        description="We combine deep web development expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      <TechRecruitingAgency 
        title="Is Web Development Talent Scarcity Slowing Your Product Roadmap?"
        description="The demand for skilled web developers has never been higher — yet the market is flooded with candidates who overstate their abilities, lack real-world project depth, or simply don't fit your technology stack. Generalist recruiters can't distinguish a senior React engineer from a junior JavaScript developer, leading to mis-hires that set timelines back by months."
        problemTitle="The Real Cost of a Wrong Web Development Hire"
        problemItems={problemItems}
        solutionTitle="Kapil Tech: Your Dedicated Web Development Staffing Partner"
        solutionDescription="Kapil Tech maintains a focused web development staffing practice with recruiters who understand the real distinctions between front-end, back-end, and full-stack roles — and who assess candidates on actual coding ability, framework depth, and real project delivery experience. We match talent to your exact technology stack, not just a job title."
        solutionPoints={solutionPoints}
        ctaText="Find Your Web Development Talent Today"
      />

      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your web development initiatives and achieve measurable business results through our expert talent solutions."
        outcomes={outcomes}
      />

      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="Web Developer Experts?"
        description="Share your web development staffing requirements today. Our team responds within hours with pre-vetted developers matched to your stack, product, and team culture."
      />
    </div>
  )
}

export default WebStaffHome;
